import { isPlayedResult, type ResultCode } from '../model/types.js';
import { allocateColours, colourState } from './colour.js';
import { bergerSchedule } from './roundrobin.js';
import {
  PairingError,
  type Pair,
  type PairingEngine,
  type PairingPlayer,
  type PairingRequest,
  type PairingResult,
} from './types.js';

/** Pair players in order, avoiding (if possible) rematches, by backtracking. */
function pairInOrder(
  order: readonly PairingPlayer[],
  canMeet: (a: PairingPlayer, b: PairingPlayer) => boolean,
): [PairingPlayer, PairingPlayer][] | null {
  if (order.length === 0) return [];
  const [first, ...rest] = order as [PairingPlayer, ...PairingPlayer[]];
  for (let i = 0; i < rest.length; i++) {
    const b = rest[i] as PairingPlayer;
    if (!canMeet(first, b)) continue;
    const sub = pairInOrder(
      rest.filter((_, j) => j !== i),
      canMeet,
    );
    if (sub !== null) return [[first, b], ...sub];
  }
  return null;
}

function opponentsOf(p: PairingPlayer): Set<number> {
  return new Set(
    p.history
      .filter((e) => e.opponent !== null && isPlayedResult(e.result))
      .map((e) => e.opponent as number),
  );
}

function colourPair(a: PairingPlayer, b: PairingPlayer, initial: 'w' | 'b'): Pair {
  const c = allocateColours(colourState(a.history), colourState(b.history), a.id, initial);
  return c === 'w' ? { white: a.id, black: b.id } : { white: b.id, black: a.id };
}

/**
 * Keizer system (club season play). Every player has a Keizer value that
 * depends on their current Keizer ranking: the leader is worth 2N, the last
 * player N + 1. A win earns the opponent's value, a draw half of it; a
 * player's own value counts once; a bye earns half of the own value, a
 * requested half-point bye a third, an absence nothing. Values and rankings
 * are recomputed until they settle. Pairing goes down the Keizer ranking,
 * pairing each player with the next one not met yet.
 */
export function keizerScores(players: readonly PairingPlayer[]): Map<number, number> {
  const n = players.length;
  const value = (rank: number): number => 2 * n + 1 - rank;
  let ranking = [...players].sort((a, b) => a.id - b.id).map((p) => p.id);
  let scores = new Map<number, number>();
  for (let iter = 0; iter < 20; iter++) {
    const val = new Map(ranking.map((id, i) => [id, value(i + 1)]));
    const next = new Map<number, number>();
    for (const p of players) {
      const own = val.get(p.id) as number;
      let s = own;
      for (const e of p.history) {
        const opp = e.opponent === null ? undefined : val.get(e.opponent);
        s += keizerPoints(e.result, own, opp ?? 0);
      }
      next.set(p.id, s);
    }
    const nextRanking = [...players]
      .sort((a, b) => (next.get(b.id) as number) - (next.get(a.id) as number) || a.id - b.id)
      .map((p) => p.id);
    scores = next;
    if (nextRanking.every((id, i) => id === ranking[i])) break;
    ranking = nextRanking;
  }
  return scores;
}

function keizerPoints(r: ResultCode, own: number, opp: number): number {
  switch (r) {
    case '1':
    case 'W':
      return opp;
    case '=':
    case 'D':
      return opp / 2;
    case '+':
    case 'U':
    case 'F':
      return own / 2;
    case 'H':
      return own / 3;
    default:
      return 0;
  }
}

export class KeizerEngine implements PairingEngine {
  readonly id = 'keizer';
  readonly name = 'Keizer';

  pair(request: PairingRequest): PairingResult {
    const scores = keizerScores(request.players);
    const avail = request.players
      .filter((p) => p.available)
      .sort((a, b) => (scores.get(b.id) as number) - (scores.get(a.id) as number) || a.id - b.id);
    const notes: string[] = [];
    let byes: number[] = [];
    let pool = avail;
    if (pool.length % 2 === 1) {
      const hadBye = (p: PairingPlayer): boolean => p.history.some((e) => e.result === 'U');
      const bye =
        [...pool].reverse().find((p) => !hadBye(p)) ?? (pool[pool.length - 1] as PairingPlayer);
      byes = [bye.id];
      pool = pool.filter((p) => p !== bye);
    }
    let pairs = pairInOrder(pool, (a, b) => !opponentsOf(a).has(b.id));
    if (pairs === null) {
      notes.push('every pairing repeats a game; repeats allowed this round');
      pairs = pairInOrder(pool, () => true);
    }
    if (pairs === null) throw new PairingError('no pairing possible');
    return { pairs: pairs.map(([a, b]) => colourPair(a, b, request.initialColour)), byes, notes };
  }
}

/**
 * Quads: players (in pairing-number order, i.e. by rating) are split into
 * groups of four that each play a three-round round robin. A remainder of
 * one to three players joins the last group, which then plays its own Berger
 * schedule (rounds beyond its schedule give that group no games).
 */
export class QuadsEngine implements PairingEngine {
  readonly id = 'quads';
  readonly name = 'Quads (groups of four)';

  constructor(private readonly groupSize = 4) {}

  groups(players: readonly PairingPlayer[]): PairingPlayer[][] {
    const sorted = [...players].sort((a, b) => a.id - b.id);
    const out: PairingPlayer[][] = [];
    for (let i = 0; i < sorted.length; i += this.groupSize)
      out.push(sorted.slice(i, i + this.groupSize));
    const last = out[out.length - 1];
    if (out.length > 1 && last !== undefined && last.length < this.groupSize) {
      out.pop();
      (out[out.length - 1] as PairingPlayer[]).push(...last);
    }
    return out;
  }

  pair(request: PairingRequest): PairingResult {
    const pairs: Pair[] = [];
    const byes: number[] = [];
    for (const g of this.groups(request.players)) {
      if (g.length < 2) continue;
      const r = bergerSchedule(g.length)[request.round - 1];
      if (r === undefined) continue;
      for (const p of r.pairs) {
        pairs.push({
          white: (g[p.white - 1] as PairingPlayer).id,
          black: (g[p.black - 1] as PairingPlayer).id,
        });
      }
      if (r.bye !== null) byes.push((g[r.bye - 1] as PairingPlayer).id);
    }
    return { pairs, byes, notes: [] };
  }
}

/**
 * Challenge ladder: the ladder starts in pairing-number order; when a lower
 * player beats a higher one, the winner takes the loser's place and everyone
 * between moves down one. Each round pairs neighbours (1-2, 3-4, ...),
 * shifting by one where that avoids repeating the previous round's games.
 */
export function ladderOrder(players: readonly PairingPlayer[], rounds: number): number[] {
  const order = [...players].sort((a, b) => a.id - b.id).map((p) => p.id);
  const byId = new Map(players.map((p) => [p.id, p]));
  for (let r = 0; r < rounds; r++) {
    for (const p of players) {
      const e = p.history[r];
      if (
        e === undefined ||
        e.opponent === null ||
        (e.result !== '1' && e.result !== '+' && e.result !== 'W')
      )
        continue;
      const winner = p.id;
      const loser = e.opponent;
      if (!byId.has(loser)) continue;
      const wi = order.indexOf(winner);
      const li = order.indexOf(loser);
      if (wi > li) {
        order.splice(wi, 1);
        order.splice(li, 0, winner);
      }
    }
  }
  return order;
}

export class LadderEngine implements PairingEngine {
  readonly id = 'ladder';
  readonly name = 'Challenge ladder';

  pair(request: PairingRequest): PairingResult {
    const order = ladderOrder(request.players, request.round - 1);
    const byId = new Map(request.players.map((p) => [p.id, p]));
    const avail = order.map((id) => byId.get(id) as PairingPlayer).filter((p) => p.available);
    const lastOpp = (p: PairingPlayer): number | null =>
      p.history[p.history.length - 1]?.opponent ?? null;
    let byes: number[] = [];
    let pool = avail;
    if (pool.length % 2 === 1) {
      byes = [(pool[pool.length - 1] as PairingPlayer).id];
      pool = pool.slice(0, -1);
    }
    const pairs = pairInOrder(pool, (a, b) => lastOpp(a) !== b.id) ?? pairInOrder(pool, () => true);
    if (pairs === null) throw new PairingError('no pairing possible');
    return {
      pairs: pairs.map(([a, b]) => colourPair(a, b, request.initialColour)),
      byes,
      notes: [],
    };
  }
}

/** Varma groups (FIDE C.05 Annex 2) for 9-24 players: numbers in each group A-D. */
const VARMA: Record<number, [number[], number[], number[], number[]]> = {
  10: [
    [3, 4, 8],
    [5, 7, 9],
    [1, 6],
    [2, 10],
  ],
  12: [
    [4, 5, 9, 10],
    [1, 2, 7],
    [6, 8, 12],
    [3, 11],
  ],
  14: [
    [4, 5, 6, 11, 12],
    [1, 2, 8, 9],
    [7, 10, 13],
    [3, 14],
  ],
  16: [
    [5, 6, 7, 12, 13, 14],
    [1, 2, 3, 9, 10],
    [8, 11, 15],
    [4, 16],
  ],
  18: [
    [5, 6, 7, 8, 14, 15, 16],
    [1, 2, 3, 10, 11, 12],
    [9, 13, 17],
    [4, 18],
  ],
  20: [
    [6, 7, 8, 9, 15, 16, 17, 18],
    [1, 2, 3, 11, 12, 13, 14],
    [5, 10, 19],
    [4, 20],
  ],
  22: [
    [6, 7, 8, 9, 10, 17, 18, 19, 20],
    [1, 2, 3, 4, 12, 13, 14, 15],
    [11, 16, 21],
    [5, 22],
  ],
  24: [
    [6, 7, 8, 9, 10, 11, 19, 20, 21, 22],
    [1, 2, 3, 4, 13, 14, 15, 16, 17],
    [12, 18, 23],
    [5, 24],
  ],
};

/**
 * Assign round-robin pairing numbers with the Varma tables so that players
 * of the same federation meet in the early rounds (C.05 5.2-5.3, Annex 2).
 * Federations are placed largest first (ties by federation code); each takes
 * the group with the fewest free numbers that still holds it, and numbers
 * inside a group are drawn by lot (a seeded random number generator here).
 * Returns pairing numbers by player id. Federations too big for any group
 * are split across groups.
 */
export function varmaNumbers(
  players: readonly { readonly id: string; readonly federation: string }[],
  random: () => number,
): Map<string, number> {
  const n = players.length;
  if (n < 9 || n > 24) throw new PairingError('Varma tables cover 9 to 24 players');
  const table = VARMA[n % 2 === 0 ? n : n + 1] as [number[], number[], number[], number[]];
  const free = table.map((g) => g.filter((x) => x <= n));
  const byFed = new Map<string, string[]>();
  for (const p of players) byFed.set(p.federation, [...(byFed.get(p.federation) ?? []), p.id]);
  const feds = [...byFed.entries()].sort(
    (a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]),
  );
  const out = new Map<string, number>();
  const draw = (pool: number[]): number =>
    pool.splice(Math.floor(random() * pool.length), 1)[0] as number;
  for (const [, ids] of feds) {
    let remaining = [...ids];
    while (remaining.length > 0) {
      const fits = free
        .map((g, i) => ({ i, size: g.length }))
        .filter((g) => g.size >= remaining.length);
      const target =
        fits.length > 0
          ? (fits.sort((a, b) => a.size - b.size)[0] as { i: number }).i
          : free.map((g, i) => ({ i, size: g.length })).sort((a, b) => b.size - a.size)[0]!.i;
      const group = free[target] as number[];
      const take = remaining.slice(0, group.length);
      for (const id of take) out.set(id, draw(group));
      remaining = remaining.slice(take.length);
    }
  }
  return out;
}
